import clsx from 'clsx';
import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Backdrop } from '../Backdrop/Backdrop';
import { Card } from '../Card';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { xsScreen } from '../../lib/constants';
import { Header } from './Header';
import { CloseButton } from './CloseButton';
import { ResponseMessageOptions } from '../../types/commonTypes';

import { ModalContent } from './ModalContent';
import { ModalActionHandler } from './ModalActionHandler';

interface ModalProps extends BaseProps {
  actionError?: boolean;
  actionLoading?: boolean;
  actionLoadingMessage?: string;
  actionMessages?: ResponseMessageOptions;
  actionMessageAsToast?: boolean;
  actionMessageTimeout?: number;
  actionSuccess?: boolean;
  backdropClassName?: string;
  disableBackdropClick?: boolean;
  fullWidth?: boolean;
  header?: string;
  headerIcon?: ReactNode;
  hideCloseButton?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  open: boolean;
  onClose(event?: SyntheticEvent): void;
  showLoading?: boolean;
  portalOut?: Element | DocumentFragment;
}

const Modal = ({
  actionError = false,
  actionLoading = false,
  actionLoadingMessage = 'Loading...',
  actionMessages = {
    error: {
      message: 'Error',
    },
    success: {
      message: 'Success',
    },
  },
  actionMessageAsToast = false,
  actionMessageTimeout = 3500,
  actionSuccess = false,
  children,
  className,
  disableBackdrop = false,
  disableBackdropClick = false,
  fullWidth = false,
  header,
  headerIcon,
  hideCloseButton = false,
  maxWidth = 'md',
  onClose,
  open,
  portalOut,
  showLoading = false,
  ...rest
}: ModalProps) => {
  const classes = useStyles();
  const isMobile = useMediaQuery(xsScreen);
  const [messagePopped, setMessagePopped] = useState(false);
  const [timeoutIds, setTimeoutIds] = useState<NodeJS.Timeout[]>([]);

  useEffect(() => {
    if (!open) removeMessage();
  }, [open]);

  useEffect(() => {
    if (open && !actionLoading && (actionError || actionSuccess))
      setMessagePopped(true);
  }, [actionError, actionLoading, actionSuccess, open]);

  useEffect(() => {
    if (open && messagePopped) {
      const showMessage = setTimeout(() => {
        !actionMessageAsToast ? handleClose() : null;
      }, actionMessageTimeout);
      const hideMessage = setTimeout(() => {
        setMessagePopped(false);
      }, actionMessageTimeout + 500);
      setTimeoutIds([showMessage, hideMessage]);
    }
  }, [messagePopped, open]);

  const removeMessage = () => {
    setMessagePopped(false);

    timeoutIds.forEach((id: NodeJS.Timeout) => clearTimeout(id));
  };

  const handleClose = (event?: SyntheticEvent) => {
    event?.stopPropagation();

    removeMessage();
    onClose(event);
  };

  if (!open) return null;

  return createPortal(
    <Backdrop disableBlur={disableBackdrop} open={open} onClose={handleClose}>
      <Card
        {...rest}
        className={clsx(className, 'relative overflow-hidden', {
          [classes.size.fullScreen]: isMobile,
          [classes.size.fullWidth]: !isMobile && fullWidth,
          [classes.size[maxWidth]]: !isMobile && !fullWidth && !messagePopped,
          [classes.size.sm]: !isMobile && messagePopped,
        })}
      >
        <Header icon={headerIcon} isMobile={isMobile} title={header} />

        <ModalActionHandler
          isLoading={actionLoading}
          isToast={actionMessageAsToast}
          loadingMessage={actionLoadingMessage}
          message={
            actionError
              ? actionMessages.error.message
              : actionMessages.success.message
          }
          messagePopped={messagePopped}
          responseType={actionError ? 'error' : 'success'}
          showLoading={showLoading}
          subMessage={
            actionError
              ? actionMessages.error.subMessage
              : actionMessages.success.subMessage
          }
        >
          {children}
        </ModalActionHandler>

        <CloseButton
          isMobile={isMobile}
          onClick={handleClose}
          hideCloseButton={hideCloseButton}
        />
      </Card>
    </Backdrop>,
    portalOut ?? document.body
  );
};

const useStyles = () => ({
  size: {
    fullScreen: 'w-screen !h-[100dvh]',
    fullWidth: 'w-full',
    lg: 'w-full md:5/6 lg:w-3/4',
    md: 'w-full sm:w-3/4 lg:!w-1/2',
    sm: 'w-full md:w-2/5 lg:w-1/4 min-w-[400px]',
    xs: 'w-full lg:w-1/6',
  },
});

export default Modal;
export { Modal, ModalContent, Header as ModalHeader };
